import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { HeaderEspaceClientComponent } from '../header-espace-client/header-espace-client.component';
import { FooterComponent } from '../footer/footer.component';
import { FileClientService, FileClientItem, FileClientData } from '../services/fileClient.service';
import { FileConversionService } from '../services/file-conversion.service';
import { environment } from '../environment.ts/environment';

@Component({
  selector: 'app-espace-client',
  standalone: true,
  imports: [CommonModule, HeaderEspaceClientComponent, FooterComponent],
  templateUrl: './espace-client.component.html',
  styleUrl: './espace-client.component.scss'
})
export class EspaceClientComponent implements OnInit {
  supportEmail = environment.supportEmail;
  userEmail: string = '';
  userId: number = 0;
  
  // Variables pour le drag & drop
  isDragOver = false;
  selectedFile: File | null = null;
  dragOverClass = '';
  isLoading = false;

  // Variables pour la gestion des fichiers
  clientFiles: FileClientItem[] = [];
  isLoadingFiles = false;
  filesError: string = '';

  // Variables pour la conversion
  isUploading: boolean = false;
  uploadProgress: number = 0;
  uploadMessage: string = '';
  isConverting: boolean = false;
  conversionProgress: number = 0;
  conversionMessage: string = '';
  isSavingToDatabase: boolean = false;
  saveMessage: string = '';
  Math = Math; // Pour utiliser Math dans le template

  constructor(
    private fileClientService: FileClientService,
    private fileConversionService: FileConversionService,
    private meta: Meta,
    private title: Title
  ) {}

  ngOnInit(): void {
    // Définir le titre de la page (45 caractères - optimal, corrigé de 35)
    this.title.setTitle('Mon espace client – pdf-to-word.fr');
    
    // Définir les meta tags SEO (160 caractères - optimal)
    this.meta.updateTag({ name: 'description', content: 'Accédez à votre espace client pdf-to-word.fr : téléchargez vos fichiers convertis, convertissez de nouveaux PDF en Word et gérez vos abonnements en toute simplicité.' });
    this.meta.updateTag({ name: 'keywords', content: 'espace client pdf-to-word, compte utilisateur PDF Word, mes fichiers convertis, gestion abonnement conversion PDF, téléchargement fichier Word' });
    
    // Meta tags supplémentaires pour le SEO
    this.meta.updateTag({ property: 'og:title', content: 'Espace client – pdf-to-word.fr' });
    this.meta.updateTag({ property: 'og:description', content: 'Accédez à votre espace client pdf-to-word.fr : téléchargez vos fichiers convertis, convertissez de nouveaux PDF en Word et gérez vos abonnements en toute simplicité.' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });

    // Récupérer l'email de l'utilisateur depuis localStorage (uniquement côté client)
    if (typeof window !== 'undefined' && window.localStorage) {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          this.userEmail = user.email || '';
          this.userId = user.id || 0;
          
          // Charger les fichiers du client si l'ID est disponible
          if (this.userId > 0) {
            this.loadClientFiles();
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des données utilisateur:', error);
        }
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
    this.dragOverClass = 'drag-over';
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.dragOverClass = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.dragOverClass = '';

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.handleFileSelection(file);
    }
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  private handleFileSelection(file: File): void {
    // Vérifier que le fichier est un PDF
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      this.selectedFile = file;
      console.log('Fichier PDF sélectionné:', file.name);
    } else {
      alert('Veuillez sélectionner un fichier PDF valide.');
    }
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  startConversion(): void {
    if (this.selectedFile) {
      console.log('Démarrage de la conversion PDF vers Word pour:', this.selectedFile.name);
      
      // Stocker les données de conversion dans le service
      this.fileConversionService.setConversionData({
        filename: this.selectedFile.name,
        size: parseFloat((this.selectedFile.size / 1024 / 1024).toFixed(2)),
        originalFormat: '.pdf',
        targetFormat: '.docx',
        file: this.selectedFile
      });

      // Lancer l'upload et la conversion
      this.uploadFileAndConvert();
    }
  }

  /**
   * Upload le fichier et lance la conversion
   */
  uploadFileAndConvert(): void {
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadMessage = 'Préparation de l\'upload...';

    // Simuler une progression pour l'interface utilisateur
    const progressInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += Math.random() * 10;
        this.uploadMessage = `Upload en cours... ${Math.round(this.uploadProgress)}%`;
      }
    }, 200);

    this.fileConversionService.uploadCurrentFile().subscribe({
      next: (response) => {
        clearInterval(progressInterval);
        this.uploadProgress = 100;
        this.uploadMessage = 'Upload terminé avec succès!';
        
        setTimeout(() => {
          this.isUploading = false;
          this.uploadMessage = '';
        }, 1000);

        const filename = response.data.fileName;
        const originalName = response.data.originalName;
        console.log('Upload réussi - Filename:', filename);

        // Lancer la conversion du fichier après l'upload
        this.convertFile(filename, originalName);
      },
      error: (error) => {
        clearInterval(progressInterval);
        this.isUploading = false;
        this.uploadMessage = 'Erreur lors de l\'upload du fichier';
        console.error('Erreur upload:', error);
        alert('Erreur lors de l\'upload du fichier. Veuillez réessayer.');
      }
    });
  }

  /**
   * Convertit le fichier uploadé
   */
  convertFile(filename: string, originalName: string): void {
    this.isConverting = true;
    this.conversionProgress = 0;
    this.conversionMessage = 'Conversion du fichier en cours...';

    // Simuler une progression pour l'interface utilisateur
    const progressInterval = setInterval(() => {
      if (this.conversionProgress < 90) {
        this.conversionProgress += Math.random() * 10;
        this.conversionMessage = `Conversion en cours... ${Math.round(this.conversionProgress)}%`;
      }
    }, 200);

    this.fileConversionService.convertFile(filename).subscribe({
      next: (response) => {
        clearInterval(progressInterval);
        this.conversionProgress = 100;
        this.conversionMessage = 'Conversion terminée avec succès!';
        
        setTimeout(() => {
          this.isConverting = false;
          this.conversionMessage = '';
        }, 1000);

        console.log('Conversion réussie:', response);

        // Envoi de l'email avec le fichier converti
        this.fileConversionService.sendConvertedEmail({
          to: this.userEmail,
          name: originalName,
          filePath: environment.fileConverterBaseUrl + response.data.downloadUrl,
          siteUrl: 'pdf-to-word.fr',
          siteName: 'PDF to Word',
        }).subscribe();
        
        // Enregistrer le fichier converti en base de données
        this.saveFileToDatabase(response.data.convertedFileName, response.data.convertedFileName);
      },
      error: (error) => {
        clearInterval(progressInterval);
        this.isConverting = false;
        this.conversionMessage = 'Erreur lors de la conversion du fichier';
        console.error('Erreur conversion:', error);
        alert('Erreur lors de la conversion du fichier. Veuillez réessayer.');
      }
    });
  }

  /**
   * Sauvegarde le fichier converti en base de données
   */
  saveFileToDatabase(convertedFileName: string, downloadUrl: string): void {
    this.isSavingToDatabase = true;
    this.saveMessage = 'Sauvegarde en base de données...';

    // Utiliser le nom réel du fichier uploadé par le client
    const originalFileName = this.selectedFile ? this.selectedFile.name : convertedFileName;

    // Préparer les données pour l'insertion
    const fileClientData: FileClientData = {
      id_client: this.userId,
      filename: originalFileName,
      filepath: downloadUrl
    };

    // Appeler le service pour sauvegarder en base de données
    this.fileClientService.createFileClientWithValidation(fileClientData).subscribe({
      next: (response) => {
        this.isSavingToDatabase = false;
        this.saveMessage = 'Fichier sauvegardé avec succès!';
        
        // Effacer le message après 2 secondes
        setTimeout(() => {
          this.saveMessage = '';
        }, 2000);

        // Recharger la liste des fichiers après sauvegarde
        this.loadClientFiles();
        
        // Réinitialiser le fichier sélectionné
        this.selectedFile = null;
        
        console.log('Fichier sauvegardé avec succès:', response);
      },
      error: (error) => {
        this.isSavingToDatabase = false;
        this.saveMessage = 'Erreur lors de la sauvegarde';
        console.error('Erreur lors de la sauvegarde en base de données:', error);
        
        // Effacer le message d'erreur après 5 secondes
        setTimeout(() => {
          this.saveMessage = '';
        }, 5000);
        
        alert('Erreur lors de la sauvegarde du fichier. Veuillez réessayer.');
      }
    });
  }

  /**
   * Charge la liste des fichiers du client
   */
  loadClientFiles(): void {
    if (this.userId <= 0) {
      console.warn('ID utilisateur non valide pour charger les fichiers');
      return;
    }

    this.isLoadingFiles = true;
    this.filesError = '';

    this.fileClientService.getFilesClientByClientIdWithValidation(this.userId)
      .subscribe({
        next: (response) => {
          this.clientFiles = response.data.files;
          this.isLoadingFiles = false;
          console.log('Fichiers chargés:', this.clientFiles.length);
        },
        error: (error) => {
          this.isLoadingFiles = false;
          this.filesError = error.message || 'Erreur lors du chargement des fichiers';
          console.error('Erreur lors du chargement des fichiers:', error);
        }
      });
  }

  /**
   * Recharge la liste des fichiers
   */
  refreshFiles(): void {
    this.loadClientFiles();
  }

  /**
   * Formate la date pour l'affichage
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Obtient le nom du fichier sans l'extension
   */
  getFileNameWithoutExtension(filename: string): string {
    return filename.replace(/\.[^/.]+$/, '');
  }

  /**
   * Télécharge un fichier
   */
  downloadFile(file: FileClientItem): void {
    try {
      // Construire l'URL complète du fichier (même approche que success.component)
      const downloadUrl = environment.fileConverterBaseUrl + environment.fileConverterDownloadUrl + file.filepath;
      
      // Créer un lien de téléchargement temporaire
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.filename; // Nom du fichier pour le téléchargement
      link.target = '_blank'; // Ouvrir dans un nouvel onglet si nécessaire
      
      // Ajouter le lien au DOM, cliquer dessus, puis le supprimer
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Téléchargement initié pour:', file.filename);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert(`Erreur lors du téléchargement de ${file.filename}`);
    }
  }
}
